import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Media } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit, Trash2 } from 'react-feather';
import CityDataModal from './CityDataModal';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, fetchTableData } from '../../../../Api/ApiMethods';

const Cities = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]); // filtered data shown in the table
    const [fullData, setFullData] = useState([]); // complete list fetched once
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedCity, setSelectedCity] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const PAGE_SIZE = 1000; // fetch a large page once and filter on the client
    const cityApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/city';

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const applyFilter = useCallback((items, term) => {
        const normalized = term.trim().toLocaleLowerCase('tr-TR');
        if (!normalized) return items;
        return items.filter(item => (item.title || '').toLocaleLowerCase('tr-TR').includes(normalized));
    }, []);

    const handleSearch = (event) => {
        event.preventDefault();
        setSearch(event.target.value);
    };

    const loadCities = useCallback(async () => {
        setIsLoading(true);
        try {
            await fetchTableData({
                apiEndpoint: cityApiUrl,
                page: 1,
                pageSize: PAGE_SIZE,
                setDataCallback: (items) => {
                    setFullData(items);
                    setData(items);
                },
            });
        } finally {
            setIsLoading(false);
        }
    }, [cityApiUrl, PAGE_SIZE]);

    useEffect(() => {
        loadCities();
    }, [loadCities]);

    useEffect(() => {
        setData(applyFilter(fullData, search));
    }, [search, fullData, applyFilter]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const openAddModal = () => {
        setModalMode('add');
        setSelectedCity(null);
        setModalOpen(true);
    };

    const openModifyModal = (city) => {
        setModalMode('modify');
        setSelectedCity(city);
        setModalOpen(true);
    };

    const columns = [
        { name: 'Sıra', selector: row => row.order, sortable: true, width: '100px' },
        { name: 'Şehir Adı', selector: row => row.title, sortable: true },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning"
                    onClick={() => openModifyModal(row)}
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Hazır Girdi Alanları' title='Şehirler' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Şehirler</H5>
                                <span>Tanımlanmış olan şehirler aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Şehir adı ara"
                                                value={search}
                                                onChange={(e) => handleSearch(e)}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <Media body className='text-end mt-3'>
                                        <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1"/>
                                            {' Şehir Ekle'}
                                        </div>
                                    </Media>
                                    {(selectedRows.length !== 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1"/>
                                            {' Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        selectableRows
                                        clearSelectedRows={clearSelectedRows}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : <div className="p-4 text-center">Şehir bulunamadı.</div>}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <CityDataModal
                apiEndpoint={cityApiUrl}
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                mode={modalMode}
                selectedCity={selectedCity}
                refreshData={loadCities}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="title"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: cityApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: loadCities,
                    })
                }
            />
        </>
    );
};

export default Cities;
